import { Injectable, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { AddPaymentMethodRequest } from '@cart/shared-types';

// Payment methods are stored in PlatformSetting keyed per user as
// "payment_methods:{userId}" with a JSON array value.
// This avoids a schema migration while keeping the feature fully functional.
// Replace with a dedicated table when payment provider integration is added.

const key = (userId: string) => `payment_methods:${userId}`;

interface StoredMethod {
  id: string;
  label: string;
  type: 'card' | 'bank';
  brand?: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  bankName?: string;
  isDefault: boolean;
  token?: string;
  createdAt: string;
}

@Injectable()
export class PaymentMethodsService {
  constructor(private readonly prisma: PrismaService) {}

  private async readMethods(userId: string): Promise<StoredMethod[]> {
    const setting = await this.prisma.platformSetting.findUnique({
      where: { key: key(userId) },
      select: { value: true },
    });
    return ((setting?.value as unknown) as StoredMethod[] | null) ?? [];
  }

  private async writeMethods(userId: string, methods: StoredMethod[]): Promise<void> {
    await this.prisma.platformSetting.upsert({
      where: { key: key(userId) },
      create: { key: key(userId), value: methods as object[], isActive: true, createdBy: userId, updatedBy: userId },
      update: { value: methods as object[], updatedBy: userId },
    });
  }

  async list(userId: string) {
    const methods = await this.readMethods(userId);
    const items = methods.map(({ token: _t, ...m }) => m);
    return { items, total: items.length };
  }

  async add(userId: string, dto: AddPaymentMethodRequest) {
    const methods = await this.readMethods(userId);
    const newMethod: StoredMethod = {
      id:        crypto.randomUUID(),
      label:     dto.label,
      type:      dto.type,
      brand:     dto.brand,
      last4:     dto.last4 ?? '0000',
      expMonth:  dto.expMonth,
      expYear:   dto.expYear,
      bankName:  dto.bankName,
      isDefault: dto.makeDefault ?? methods.length === 0,
      token:     dto.token,
      createdAt: new Date().toISOString(),
    };

    if (newMethod.isDefault) {
      methods.forEach(m => { m.isDefault = false; });
    }

    methods.push(newMethod);
    await this.writeMethods(userId, methods);
    const { token: _t, ...result } = newMethod;
    return result;
  }

  async remove(userId: string, methodId: string) {
    const methods = await this.readMethods(userId);
    const idx = methods.findIndex(m => m.id === methodId);
    if (idx === -1) throw new NotFoundException('Payment method not found');

    const [removed] = methods.splice(idx, 1);
    // If removed was default, promote the first remaining
    if (removed.isDefault && methods.length > 0) methods[0].isDefault = true;

    await this.writeMethods(userId, methods);
    return { success: true };
  }
}
